import { useRouter } from 'expo-router'
import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { PublicationCard, type TPublicationCardProps } from '@/src/features/publication/ui/components/PublicationCard'
import { usePublicationHandlers } from '@/src/features/publication/ui/components/PublicationCard/usePublicationHandlers'
import { PublicationSeed } from '@/src/features/publication/ui/components/PublicationSeed/PublicationSeed'
import { ICON } from '@/src/general/enums/icons'
import { useTrackDisplayed } from '@/src/general/hooks/useTrackOnce'
import { GTM } from '@/src/general/utils/gtm'
import { EVENTS, PUBLICATION_TRACKING_SOURCE, TRACKING_SOURCE } from '@/src/general/utils/gtm/constants'
import { useRoutes } from '@/src/general/utils/routes'
import { stopPropagation } from '@/src/general/utils/stopPropagation'

import { COMMUNITY_LIFETIME } from '../../../models/community.constants'
import { SubscribeJourneyLayout } from '../../layouts/SubscribeJourneyLayout'

type TCommunityJoinNextFirstPublicationProps = Pick<TPublicationCardProps, 'publication'>

type TJoinNextPublicationCardProps = Pick<TPublicationCardProps, 'publication'>

const JoinNextPublicationCard = ({ publication }: TJoinNextPublicationCardProps): ReactNode => {
  const { onCardPress } = usePublicationHandlers({
    publication,
    trackingSource: TRACKING_SOURCE.COMMUNITY_JOIN,
    publicationTrackingSource: PUBLICATION_TRACKING_SOURCE.COMMUNITY_READ_MORE,
    cardClickEvent: EVENTS.COMMUNITY_JOINED_NEXT_STARTED_PRAY_PUBLICATION_CLICKED,
    trackingVariables: { communityInfos: { community_id: publication.community.id } },
  })

  return (
    <PublicationCard
      publication={publication}
      showOptions={false}
      cardClickEvent={EVENTS.COMMUNITY_JOINED_NEXT_STARTED_PRAY_PUBLICATION_CLICKED}
      trackingSource={TRACKING_SOURCE.COMMUNITY_JOIN}
      publicationTrackingSource={PUBLICATION_TRACKING_SOURCE.COMMUNITY_READ_MORE}
      footer={
        <Button variant="solid" colors="primary" fullWidth startIcon={ICON.FIRE} onPress={stopPropagation(onCardPress)}>
          <FormattedMessage id="community.join-next.pray-now" />
        </Button>
      }
    />
  )
}

export const CommunityJoinNextFirstPublication = ({
  publication: publicationItem,
}: TCommunityJoinNextFirstPublicationProps): ReactNode => {
  const isEverlasting = publicationItem.community.lifetime === COMMUNITY_LIFETIME.EVERLASTING
  const router = useRouter()
  const { routes } = useRoutes()

  useTrackDisplayed(EVENTS.COMMUNITY_JOINED_NEXT_STARTED_DISPLAYED, publicationItem.community.id, {
    communityInfos: { community_id: publicationItem.community.id },
  })

  const handlePress = () => {
    router.navigate(routes['prayer-space']())
    GTM.sendEvent(EVENTS.COMMUNITY_JOINED_NEXT_STARTED_CLOSED, {
      communityInfos: { community_id: publicationItem.community.id },
    })
  }

  return (
    <SubscribeJourneyLayout
      title={
        isEverlasting ? (
          <FormattedMessage id="community.join-next.everlasting.start" />
        ) : (
          <FormattedMessage id="community.join-next.start" values={{ lifetime: publicationItem.community.lifetime }} />
        )
      }
    >
      <View className="w-full gap-4">
        <PublicationSeed initialPublicationId={publicationItem.id}>
          {({ data: publicationSeed = publicationItem }) => <JoinNextPublicationCard publication={publicationSeed} />}
        </PublicationSeed>
      </View>

      <Button endIcon={ICON.CHEVRON_RIGHT} variant="light" colors="primary" onPress={handlePress}>
        <FormattedMessage id="community.join-next.start-later" />
      </Button>
    </SubscribeJourneyLayout>
  )
}
